import { flow, SkipMode } from '@1x2network/flow'
import { BasicLayout, OrientationMap } from '@1x2network/layout'
import {
    applySpecification,
    Component,
    ContainerSpecification,
    createSpine,
    createSprite,
    Spine,
    SpineSpecification,
    SpriteSpecification,
} from '@1x2network/pixi_assets'
import gsap from 'gsap'
import { Container, Sprite } from 'pixi.js'

/**
 * Background sprite for the game, with a tiered spine animation on top. Animation changes as the excitement changes.
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class Background extends Component<BackgroundSpec> {
    private backgroundSprite: Sprite
    private backgroundSpine: Spine
    private layout: BasicLayout

    constructor(spec: string, parent: Container, layout: BasicLayout) {
        super({ spec, parent })
        this.layout = layout
        this.backgroundSprite = createSprite({ spec: this.specification.sprite, parent: this })
        this.backgroundSpine = createSpine({ spec: this.specification.spine, parent: this.backgroundSprite })
        layout.onResize.connect(this.reposition.bind(this))
        this.reposition()
        this.playIdle()
    }

    async playScatter() {
        const animation = this.specification.win
        await flow.wait(this.backgroundSpine.play(animation))
    }

    async playWin() {
        const animation = this.specification.win
        await flow.wait(this.backgroundSpine.play(animation), { skipMode: SkipMode.Unskippable }), this.playIdle()
    }

    public playIdle() {
        const loop = this.specification.loop
        this.backgroundSpine.play(loop, 0, true)
    }

    reposition() {
        const { orientation, left, right, top, bottom } = this.layout
        const size = this.specification.size
        const sprite = this.backgroundSprite

        this.playIdle()

        applySpecification(sprite, this.specification.sprite[orientation])
        const anchorX = gsap.utils.mapRange(-size.width / 2, size.width / 2, 0, 1, sprite.pivot.x)
        const anchorY = gsap.utils.mapRange(-size.height / 2, size.height / 2, 0, 1, sprite.pivot.y)

        // Make sure we don't divide by zero
        if (!anchorX || anchorX === 1 || !anchorY || anchorY === 1) {
            return
        }

        const negativeXScaleFactor = size.width * anchorX
        const positiveXScaleFactor = size.width * (1 - anchorX)
        const negativeYScaleFactor = size.height * anchorY
        const positiveYScaleFactor = size.height * (1 - anchorY)

        const distanceToLeft = Math.abs(left - sprite.x)
        const distanceToRight = Math.abs(sprite.x - right)
        const distanceToTop = Math.abs(top - sprite.y)
        const distanceToBottom = Math.abs(bottom - sprite.y)

        const leftScale = distanceToLeft / negativeXScaleFactor
        const rightScale = distanceToRight / positiveXScaleFactor
        const topScale = distanceToTop / negativeYScaleFactor
        const bottomScale = distanceToBottom / positiveYScaleFactor

        const scale = Math.max(leftScale, rightScale, topScale, bottomScale)
        this.backgroundSprite.scale.set(scale)
    }

    public reset() {
        this.backgroundSpine.reset()
    }
}

export interface BackgroundSpec extends ContainerSpecification {
    sprite: SpriteSpecification & OrientationMap<ContainerSpecification>
    spine: SpineSpecification
    win: string
    loop: string
    size: { height: number; width: number }
}
